import { Button, Collapse, Fade, Link, Stack, Typography, styled, useMediaQuery } from "@mui/material";
import { ICustomerRelationship } from "@smartrr/shared/entities/CustomerRelationship";
import { SmartrrLogo } from "@smartrr/shared/icons/SmartrrLogo";
import { capitalizeFirstLetter } from "@smartrr/shared/utils/capitalizeFirstLetter";
import { getFromLocalStorage } from "@smartrr/shared/utils/localStorageAccess";
import React, { useCallback, useState } from "react";

import { validationInLocalStorageKey } from "@customer-app/state/reducers/auth";

import "../account/fonts.css";

const PreviewBanner = styled(Stack)`
  align-items: center;
  background-color: rgba(36, 36, 36, 0.9);
  border-radius: 22px;
  height: 84px;
  padding: 22px;
  width: 100%;

  span {
    display: flex;
    align-items: center;
    cursor: pointer;
    &#smartrr-r svg {
      border: 0.5px dashed #dedede;
      box-shadow: 0px 0px 10px rgba(209, 209, 209, 0.25);
      border-radius: 7px;
    }
  }

  p[class*="body"],
  a[class*="body"],
  button {
    color: #ffffff;
    font-family: "Inter";
    font-size: 14px;
    text-transform: none;
    &[class*="body2"] {
      font-weight: 700;
    }
  }

  a {
    cursor: pointer !important;
    text-decoration: underline;
    text-decoration-color: #ffffff !important;
  }

  button {
    align-items: center;
    background: rgba(254, 254, 254, 0.8);
    border-radius: 40px;
    color: #000;
    display: flex;
    flex-direction: row;
    justify-content: center;
    padding: 10px 20px;
    width: max-content;
    &:hover {
      background-color: #c7c7c7;
    }
  }
`;

const BannerWrapper = styled(Stack)<{ isMobile: boolean; isOpen: boolean; offset: boolean }>`
  bottom: ${props => (props.isMobile ? "70px" : "18px")};
  height: 84px;
  left: ${({ offset }) => (offset ? "95px" : "20px")};
  position: fixed;
  right: 20px;
  transition: all 1s ease-in-out;
  width: ${({ isOpen }) => (isOpen ? "97%" : "6%")};
  z-index: 6001;

  [class*="MuiCollapse"] {
    width: 100%;
  }
  [class*="MuiCollapse-entered"] {
    width: auto !important;
  }
`;

export const PreviewAsCustomerBanner = ({ firstName, lastName }: ICustomerRelationship) => {
  const [isBannerOpen, setIsBannerOpen] = useState(true);
  const [showContent, setShowContent] = useState(true);
  const isMobile = useMediaQuery("(max-width:600px)");

  const shopifyAdminBar = document.querySelector("[aria-label='Toggle admin bar']");

  const toggleBanner = useCallback(() => {
    setIsBannerOpen(prev => !prev);
    if (isMobile || !isBannerOpen) {
      setTimeout(() => {
        setShowContent(prev => !prev);
      }, 1000);
    } else {
      setShowContent(prev => !prev);
    }
  }, [isMobile, isBannerOpen]);

  const onExitPreview = useCallback(() => (window.location.href = "/account"), []);

  const validationFromLocalStorage = JSON.parse(getFromLocalStorage(validationInLocalStorageKey));
  if (!validationFromLocalStorage?.adminShopifyId) {
    return <React.Fragment />;
  }

  return (
    <BannerWrapper isMobile={isMobile} isOpen={isBannerOpen} offset={!!shopifyAdminBar}>
      <Collapse orientation="horizontal" in={isBannerOpen} collapsedSize={82}>
        <PreviewBanner direction="row" justifyContent="space-between">
          <Stack direction="row" spacing="20px" alignItems="center" id="info">
            <span id="smartrr-r" onClick={toggleBanner}>
              <SmartrrLogo />
            </span>
            <Fade in={showContent}>
              {showContent && firstName && lastName ? (
                <Stack direction="column">
                  <Typography variant="body1">You&apos;re viewing this account portal as:</Typography>
                  <Typography variant="body2">{capitalizeFirstLetter(`${firstName} ${lastName}`)}</Typography>
                </Stack>
              ) : (
                <div />
              )}
            </Fade>
          </Stack>
          <Fade in={showContent}>
            {showContent ? (
              <Stack direction="row" id="actions" alignItems="center" spacing="10px">
                <Button onClick={onExitPreview}>Exit customer view</Button>
                {isMobile ? null : (
                  <Link onClick={toggleBanner} variant="body1">
                    Hide bar
                  </Link>
                )}
              </Stack>
            ) : (
              <div />
            )}
          </Fade>
        </PreviewBanner>
      </Collapse>
    </BannerWrapper>
  );
};
